import { CHUNK_SIZE, WORLD_HEIGHT } from "../sim/world/constants";
import { blockIndex, PaletteSubChunk } from "../sim/world/palette";
import { caveEntrances } from "./caves";
import { WORLDGEN_BLOCK_ID } from "./blocks";
import { alphaRegionGraph } from "./regions";
import { terrainHeight } from "./terrain";
import type { PlacedStructure, RegionGraph, StructureBlock, StructurePrefab } from "./types";

const WAYSHRINE_BLOCKS: readonly StructureBlock[] = Object.freeze([
  { x: 0, y: 0, z: 0, block: 1 }, { x: 1, y: 0, z: 0, block: 1 }, { x: 2, y: 0, z: 0, block: 1 }, { x: 3, y: 0, z: 0, block: 1 }, { x: 4, y: 0, z: 0, block: 1 },
  { x: 0, y: 0, z: 1, block: 1 }, { x: 1, y: 0, z: 1, block: 1 }, { x: 2, y: 0, z: 1, block: 1 }, { x: 3, y: 0, z: 1, block: 1 }, { x: 4, y: 0, z: 1, block: 1 },
  { x: 0, y: 0, z: 2, block: 1 }, { x: 1, y: 0, z: 2, block: 1 }, { x: 2, y: 0, z: 2, block: 1 }, { x: 3, y: 0, z: 2, block: 1 }, { x: 4, y: 0, z: 2, block: 1 },
  { x: 0, y: 0, z: 3, block: 1 }, { x: 1, y: 0, z: 3, block: 1 }, { x: 2, y: 0, z: 3, block: 1 }, { x: 3, y: 0, z: 3, block: 1 }, { x: 4, y: 0, z: 3, block: 1 },
  { x: 0, y: 0, z: 4, block: 1 }, { x: 1, y: 0, z: 4, block: 1 }, { x: 2, y: 0, z: 4, block: 1 }, { x: 3, y: 0, z: 4, block: 1 }, { x: 4, y: 0, z: 4, block: 1 },
  { x: 0, y: 1, z: 0, block: 1 }, { x: 0, y: 2, z: 0, block: 1 }, { x: 0, y: 3, z: 0, block: 1 },
  { x: 4, y: 1, z: 0, block: 1 }, { x: 4, y: 2, z: 0, block: 1 }, { x: 4, y: 3, z: 0, block: 1 },
  { x: 0, y: 1, z: 4, block: 1 }, { x: 0, y: 2, z: 4, block: 1 }, { x: 0, y: 3, z: 4, block: 1 },
  { x: 4, y: 1, z: 4, block: 1 }, { x: 4, y: 2, z: 4, block: 1 }, { x: 4, y: 3, z: 4, block: 1 },
  { x: 1, y: 4, z: 1, block: 4 }, { x: 2, y: 4, z: 1, block: 4 }, { x: 3, y: 4, z: 1, block: 4 },
  { x: 1, y: 4, z: 2, block: 4 }, { x: 2, y: 4, z: 2, block: 4 }, { x: 3, y: 4, z: 2, block: 4 },
  { x: 1, y: 4, z: 3, block: 4 }, { x: 2, y: 4, z: 3, block: 4 }, { x: 3, y: 4, z: 3, block: 4 },
].map((block) => Object.freeze(block)));

function forestTree(originX: number, originZ: number, trunkHeight: number): StructureBlock[] {
  const blocks: StructureBlock[] = [{ x: originX, y: 0, z: originZ, block: 2 }];
  for (let y = 1; y <= trunkHeight; y += 1) {
    blocks.push({ x: originX, y, z: originZ, block: WORLDGEN_BLOCK_ID.oakLog });
  }
  for (let y = trunkHeight - 2; y <= trunkHeight + 2; y += 1) {
    const verticalDistance = Math.abs(y - trunkHeight);
    const radius = verticalDistance === 2 ? 1 : 2;
    for (let dz = -radius; dz <= radius; dz += 1) {
      for (let dx = -radius; dx <= radius; dx += 1) {
        if (Math.abs(dx) + Math.abs(dz) > radius * 2 - Math.max(0, verticalDistance - 1)) continue;
        if (dx === 0 && dz === 0 && y <= trunkHeight) continue;
        // Opaque green heartwood is fully enclosed by the alpha-cutout crown.
        // Besides breaking up large leaf volumes, it prevents distant crowns
        // from becoming visually hollow through many overlapping cutouts.
        const enclosed = radius === 2 && Math.abs(dx) <= 1 && Math.abs(dz) <= 1 && verticalDistance <= 1;
        blocks.push({
          x: originX + dx,
          y,
          z: originZ + dz,
          block: enclosed ? 3 : WORLDGEN_BLOCK_ID.oakLeaves,
        });
      }
    }
  }
  return blocks;
}

const FOREST_GROVE_BLOCKS: readonly StructureBlock[] = Object.freeze([
  ...forestTree(3, 3, 6),
  ...forestTree(12, 4, 7),
  ...forestTree(7, 12, 6),
  // Low alpha-cutout shrubs make the authored site read as a grove instead of
  // three isolated trunks while keeping the floor traversable.
  ...[[2, 9], [5, 7], [10, 10], [13, 12], [9, 2]].flatMap(([x, z]) => [
    { x: x!, y: 0, z: z!, block: WORLDGEN_BLOCK_ID.forestGroundCover },
    { x: x! + 1, y: 0, z: z!, block: WORLDGEN_BLOCK_ID.forestGroundCover },
    { x: x!, y: 0, z: z! + 1, block: WORLDGEN_BLOCK_ID.forestGroundCover },
    { x: x!, y: 1, z: z!, block: WORLDGEN_BLOCK_ID.oakLeaves },
  ]),
].map((block) => Object.freeze(block)));

export const STRUCTURE_PREFABS: Readonly<Record<string, StructurePrefab>> = Object.freeze({
  "stone-wayshrine": Object.freeze({
    id: "stone-wayshrine",
    size: Object.freeze([5, 5, 5] as const),
    foundationBlock: 1,
    blocks: WAYSHRINE_BLOCKS,
  }),
  "forest-grove": Object.freeze({
    id: "forest-grove",
    size: Object.freeze([16, 10, 16] as const),
    foundationBlock: 2,
    blocks: FOREST_GROVE_BLOCKS,
  }),
});

const STRUCTURE_CLEARANCE_FROM_ENTRANCE = 10;

function rotatedSize(prefab: StructurePrefab, rotation: 0 | 1 | 2 | 3): readonly [number, number] {
  return rotation % 2 === 0 ? [prefab.size[0], prefab.size[2]] : [prefab.size[2], prefab.size[0]];
}

function rotateBlock(prefab: StructurePrefab, block: StructureBlock, rotation: 0 | 1 | 2 | 3): readonly [number, number] {
  switch (rotation) {
    case 1: return [prefab.size[2] - 1 - block.z, block.x];
    case 2: return [prefab.size[0] - 1 - block.x, prefab.size[2] - 1 - block.z];
    case 3: return [block.z, prefab.size[0] - 1 - block.x];
    default: return [block.x, block.z];
  }
}

export function placedStructures(seed: number, graph: RegionGraph = alphaRegionGraph(seed)): readonly PlacedStructure[] {
  const normalizedSeed = seed >>> 0;
  const cached = placementCache.get(graph);
  if (cached?.seed === normalizedSeed) return cached.placements;
  const placements: PlacedStructure[] = [];
  const entrances = caveEntrances(normalizedSeed, graph);
  for (const region of graph.regions) {
    for (const site of region.sites) {
      if (site.prefabId === null) continue;
      const prefab = STRUCTURE_PREFABS[site.prefabId];
      if (!prefab) continue;
      const [width, depth] = rotatedSize(prefab, site.rotation);
      const initialX = Math.round(site.x - (width - 1) / 2);
      const initialZ = Math.round(site.z - (depth - 1) / 2);
      const offsets = [[0, 0], [14, 0], [-14, 0], [0, 14], [0, -14], [14, 14], [-14, 14], [14, -14], [-14, -14]] as const;
      const isClear = (candidateX: number, candidateZ: number): boolean => entrances.every((entrance) => {
        const nearestX = Math.max(candidateX, Math.min(entrance.x, candidateX + width - 1));
        const nearestZ = Math.max(candidateZ, Math.min(entrance.z, candidateZ + depth - 1));
        return (entrance.x - nearestX) ** 2 + (entrance.z - nearestZ) ** 2 > STRUCTURE_CLEARANCE_FROM_ENTRANCE ** 2;
      });
      const offset = offsets.find(([dx, dz]) => isClear(initialX + dx, initialZ + dz)) ?? offsets[0];
      const originX = initialX + offset[0];
      const originZ = initialZ + offset[1];
      let highestGround = 0;
      for (let z = 0; z < depth; z += 1) {
        for (let x = 0; x < width; x += 1) {
          highestGround = Math.max(highestGround, terrainHeight(normalizedSeed, originX + x, originZ + z, graph));
        }
      }
      placements.push(Object.freeze({
        siteId: site.id,
        prefab,
        originX,
        baseY: highestGround + 1,
        originZ,
        rotation: site.rotation,
      }));
    }
  }
  const frozen = Object.freeze(placements);
  placementCache.set(graph, { seed: normalizedSeed, placements: frozen });
  return frozen;
}

const placementCache = new WeakMap<RegionGraph, {
  readonly seed: number;
  readonly placements: readonly PlacedStructure[];
}>();

function writeGlobal(
  chunk: PaletteSubChunk,
  protectedBlocks: Uint8Array,
  cx: number,
  cy: number,
  cz: number,
  x: number,
  y: number,
  z: number,
  block: number,
): void {
  const localX = x - cx * CHUNK_SIZE;
  const localY = y - cy * CHUNK_SIZE;
  const localZ = z - cz * CHUNK_SIZE;
  if (localX < 0 || localX >= CHUNK_SIZE || localY < 0 || localY >= CHUNK_SIZE || localZ < 0 || localZ >= CHUNK_SIZE) return;
  chunk.set(localX, localY, localZ, block);
  protectedBlocks[blockIndex(localX, localY, localZ)] = 1;
}

/** Stamps authored blocks and generic down-to-ground foundations into one chunk. */
export function stampStructures(
  seed: number,
  graph: RegionGraph,
  placements: readonly PlacedStructure[],
  chunk: PaletteSubChunk,
  protectedBlocks: Uint8Array,
  cx: number,
  cy: number,
  cz: number,
): void {
  const chunkMinX = cx * CHUNK_SIZE;
  const chunkMinZ = cz * CHUNK_SIZE;
  const chunkMaxX = chunkMinX + CHUNK_SIZE - 1;
  const chunkMaxZ = chunkMinZ + CHUNK_SIZE - 1;
  for (const placement of placements) {
    const [width, depth] = rotatedSize(placement.prefab, placement.rotation);
    if (placement.originX > chunkMaxX || placement.originZ > chunkMaxZ
      || placement.originX + width - 1 < chunkMinX || placement.originZ + depth - 1 < chunkMinZ) continue;
    for (const block of placement.prefab.blocks) {
      const [rx, rz] = rotateBlock(placement.prefab, block, placement.rotation);
      const worldX = placement.originX + rx;
      const worldZ = placement.originZ + rz;
      const worldY = placement.baseY + block.y;
      if (block.y === 0) {
        const ground = terrainHeight(seed, worldX, worldZ, graph);
        for (let supportY = ground; supportY < placement.baseY; supportY += 1) {
          if (supportY >= 0 && supportY < WORLD_HEIGHT) {
            writeGlobal(chunk, protectedBlocks, cx, cy, cz, worldX, supportY, worldZ, placement.prefab.foundationBlock);
          }
        }
      }
      if (worldY >= 0 && worldY < WORLD_HEIGHT) {
        writeGlobal(chunk, protectedBlocks, cx, cy, cz, worldX, worldY, worldZ, block.block);
      }
    }
  }
}
